<template>
  <v-form ref="form" @submit.prevent="onSubmit">
    <v-card flat>
      <v-card-text>
        <v-autocomplete
          v-model="selectedPrediction"
          :items="predictions"
          :loading="loading"
          item-text="name"
          item-value="uuid"
          label="Select a prediction"
          hint="Choose the prediction containing the groups for this report"
          return-object
          clearable
          required
          :rules="[v => !!v || 'Prediction is required']"
        />

        <v-autocomplete
          v-model="selectedGroups"
          :items="availableGroups"
          item-text="name"
          item-value="id"
          label="Select groups"
          hint="Choose one or more groups to include in the report"
          multiple
          chips
          deletable-chips
          small-chips
          clearable
          return-object
          :disabled="!selectedPrediction"
          :rules="[v => v && v.length > 0 || 'At least one group is required']"
        />

        <v-row>
          <v-col>
            <v-select
              v-model="formData.dateFormat"
              :items="dateFormats"
              label="Date format"
              hint="Format for date columns"
              persistent-hint
              clearable
            />
          </v-col>
          <v-col cols="auto">
            <v-radio-group
              v-model="formData.dateSeparator"
              label="Date separator"
              hide-details
              row
            >
              <v-radio label="Dash (-)" value="-" />
              <v-radio label="Slash (/)" value="/" />
            </v-radio-group>
          </v-col>
          <v-col cols="auto">
            <v-radio-group
              v-model="formData.dayOfMonth"
              label="Day of month"
              hide-details
              row
            >
              <v-radio label="First" value="start" />
              <v-radio label="Last" value="end" />
            </v-radio-group>
          </v-col>
        </v-row>

        <v-checkbox
          v-model="formData.includeHeaders"
          label="Include completion header data"
          hide-details
        />

        <v-checkbox
          v-model="formData.includeAssumptions"
          label="Include economic assumptions"
          hide-details
        />
      </v-card-text>
      <v-card-actions>
        <v-btn
          depressed
          class="success"
          type="submit"
          :disabled="!isValid"
          :loading="working"
        >
          {{ isEditMode ? 'Update Report' : 'Generate Report' }}
        </v-btn>
        <v-btn text @click="$emit('cancel')">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import Predictions from "@/api/predictions"

export default {
  name: "ReportForm",
  props: {
    report: {
      type: Object,
      default: null
    },
    working: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isEditMode() {
      return !!this.report
    },
    isValid() {
      return (
        !!this.selectedPrediction &&
        !!this.fullPrediction &&
        this.selectedGroups.length > 0 &&
        !!this.formData.dateFormat &&
        !!this.formData.dateSeparator
      )
    },
    dateFormats() {
      const sep = this.formData.dateSeparator
      return [
        { text: `YYYY${sep}MM${sep}DD`, value: `YYYY${sep}MM${sep}DD` },
        { text: `MM${sep}DD${sep}YYYY`, value: `MM${sep}DD${sep}YYYY` },
        { text: `DD${sep}MM${sep}YYYY`, value: `DD${sep}MM${sep}YYYY` }
      ]
    }
  },
  watch: {
    selectedPrediction: {
      handler(newVal) {
        if (newVal && newVal.uuid) {
          this.fetchPredictionDetails(newVal.uuid)
        } else {
          this.fullPrediction = null
          this.availableGroups = []
          this.selectedGroups = []
        }
      },
      immediate: false
    },
    'formData.dateSeparator': {
      handler(newSep) {
        const currentFormat = this.formData.dateFormat
        this.formData.dateFormat = currentFormat.replace(/[/-]/g, newSep)
      }
    }
  },
  data() {
    return {
      predictions: [],
      selectedPrediction: null,
      fullPrediction: null,
      availableGroups: [],
      selectedGroups: [],
      loading: false,
      formData: {
        includeHeaders: true,
        includeAssumptions: true,
        dateFormat: 'YYYY/MM/DD',
        dateSeparator: '-',
        dayOfMonth: 'start'
      }
    }
  },
  mounted() {
    this.fetchPredictions()
    if (this.isEditMode) {
      this.loadReportData()
    }
  },
  methods: {
    fetchPredictions() {
      this.loading = true
      this.setBusy(true)
      Predictions.list(1000, 0)
        .then(response => {
          this.predictions = response.data
        })
        .catch(error => {
          this.notify('Failed to load predictions', 'red')
        })
        .finally(() => {
          this.loading = false
          this.setBusy(false)
        })
    },
    fetchPredictionDetails(uuid) {
      this.loading = true
      Predictions.retrieve(uuid)
        .then(response => {
          this.fullPrediction = response.data
          this.loadGroups(this.fullPrediction)
        })
        .catch(error => {
          this.notify('Failed to load prediction details', 'red')
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadGroups(prediction) {
      if (!prediction) return

      const groups = prediction.groups || []
      const savedGroups = groups.filter(g => g.locked && g.saved)

      this.availableGroups = savedGroups.map(g => ({
        id: g.id,
        name: g.name,
        count: g.count || 0,
        curves: !this.isEmpty(g.curves)
      }))
      
      if (!this.isEditMode) {
        this.selectedGroups = []
      }
    },
    loadReportData() {
      if (!this.report || !this.report.config) return

      const config = this.report.config
      const metadata = config.metadata || {}

      // Set form options from report config
      this.formData.includeHeaders = metadata.includeHeaders !== undefined ? metadata.includeHeaders : true
      this.formData.includeAssumptions = metadata.includeAssumptions !== undefined ? metadata.includeAssumptions : true
      this.formData.dateFormat = metadata.dateFormat || 'YYYY/MM/DD'
      this.formData.dateSeparator = metadata.dateSeparator || '-'
      this.formData.dayOfMonth = metadata.dayOfMonth || 'start'

      // Set prediction
      if (this.report.prediction) {
        const predictionData = this.report.prediction
        this.selectedPrediction = {
          id: predictionData.id,
          uuid: predictionData.uuid,
          name: predictionData.name
        }
      }

      // Set selected groups after prediction is loaded
      if (config.groups && config.groups.length > 0) {
        this.$nextTick(() => {
          this.selectedGroups = config.groups.map(g => ({
            id: g.id,
            name: g.name,
            count: g.count || 0
          }))
        })
      }
    },
    onSubmit() {
      if (!this.isValid) {
        this.notify('Please complete all required fields', 'red')
        return
      }

      if (this.selectedGroups.length === 0) {
        this.notify('Please select at least one group to include in the report', 'red')
        return
      }

      // Get the full groups data from the prediction
      const selectedGroupNames = this.selectedGroups.map(g => g.name)
      const selectedGroups = this.fullPrediction.groups.filter(g =>
        selectedGroupNames.includes(g.name)
      )

      // Ensure each group has economics configuration
      const defaultEconomics = {
        oil_price: 60.0,
        gas_price: 3.0,
        initial_capex: 0.0,
        fixed_cost: 0.0,
        discount_rate: 10.0,
        shrinkage: 0.0,
        working_interest: 2.5,
        net_revenue_interest: 1.9,
        severance_tax_oil: 4.6,
        severance_tax_gas: 7.5,
        ad_valorem_tax: 2.5,
        expense_oil: 10.0,
        expense_gas: 10.0,
        expense_water: 10.0,
        aggregation_month: new Date().toISOString().substring(0, 10)
      }

      selectedGroups.forEach(group => {
        if (!group.economics) {
          group.economics = JSON.parse(JSON.stringify(defaultEconomics))
        }
      })

      const reportData = {
        name: `${this.selectedPrediction.name} Economics Report`,
        prediction: this.selectedPrediction.id,
        config: {
          groups: selectedGroups,
          metadata: {
            includeHeaders: this.formData.includeHeaders,
            includeAssumptions: this.formData.includeAssumptions,
            dateFormat: this.formData.dateFormat,
            dateSeparator: this.formData.dateSeparator,
            dayOfMonth: this.formData.dayOfMonth
          }
        }
      }

      this.$emit('submit', reportData)
    }
  }
}
</script>
